import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import DataTable from '@/components/DataTable';
import MultiSelect from '@/components/MultiSelect';
import { toast } from 'sonner';
import axios from 'axios';
import {
  RefreshCw, Boxes, Warehouse, Store, AlertTriangle, TrendingUp, PackageX,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ZAxis, Legend,
} from 'recharts';
import {
  buildSkuIntelligence, aggregateChannelKpis, classifyStatus, parseNumber,
  CHANNEL_LIST, STATUS_STYLES, DOH_CELL, dn,
} from '@/lib/channel-intel';
import { isExcludedFromTotals } from '@/lib/inventory-exclusions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Wellversed palette rotation for charts
const PIE_COLORS = ['#2EB4AC', '#0F766E', '#3B82F6', '#60A5FA', '#22C55E', '#86EFAC', '#A855F7', '#F59E0B'];

const formatInt = (v) => Math.round(v || 0).toLocaleString('en-IN');
const formatINR = (v) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(v || 0);
const formatDoh = (doh, stock) => (!stock || stock === 0 || !doh) ? '—' : Math.round(doh).toString();

export default function DashboardPage() {
  const navigate = useNavigate();
  const [overviewRows, setOverviewRows] = useState([]);
  const [stocksRows, setStocksRows] = useState([]);
  const [allocationRows, setAllocationRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brandFilter, setBrandFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('radar_session_token');
    if (!token) { navigate('/'); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('radar_session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [ov, st, al] = await Promise.all([
        axios.get(`${API}/sheets/data`, { headers }),
        axios.get(`${API}/sheets/stocks`, { headers }),
        axios.get(`${API}/sheets/allocation`, { headers }),
      ]);
      setOverviewRows(ov.data.rows || []);
      setStocksRows(st.data.rows || []);
      setAllocationRows(al.data.rows || []);
      toast.success('Dashboard data loaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Build overview dict rows for brand filtering
  const overviewDicts = useMemo(() => {
    if (!overviewRows.length) return [];
    const headers = overviewRows[0];
    return overviewRows.slice(1).map(r => {
      const o = {}; headers.forEach((h, i) => { o[h] = r[i] !== undefined ? r[i] : ''; }); return o;
    });
  }, [overviewRows]);

  const brands = useMemo(
    () => [...new Set(overviewDicts.map(r => r.BRAND).filter(Boolean))],
    [overviewDicts]
  );

  // Filter overview rows by brand and rebuild an overview subset for the intel pipeline
  const { filteredOverviewRows, skuWhitelist } = useMemo(() => {
    if (brandFilter === 'all' || !overviewRows.length) {
      return { filteredOverviewRows: overviewRows, skuWhitelist: null };
    }
    const headers = overviewRows[0];
    const brandIdx = headers.findIndex(h => (h || '').toString().trim().toUpperCase() === 'BRAND');
    const skuIdx = headers.findIndex(h => ['sku', 'title', 'product name'].includes((h || '').toString().trim().toLowerCase()));
    const keep = [headers];
    const whitelist = new Set();
    overviewRows.slice(1).forEach(r => {
      if ((r[brandIdx] || '').toString().trim() === brandFilter) {
        keep.push(r);
        if (skuIdx !== -1 && r[skuIdx]) whitelist.add((r[skuIdx] || '').toString().trim());
      }
    });
    return { filteredOverviewRows: keep, skuWhitelist: whitelist };
  }, [overviewRows, brandFilter]);

  // Build per-SKU intelligence using filtered overview
  const { skus, overview } = useMemo(
    () => buildSkuIntelligence({
      overviewRows: filteredOverviewRows,
      stocksRows,
      allocationRows,
    }),
    [filteredOverviewRows, stocksRows, allocationRows]
  );

  // If brand whitelist is active, restrict skus to those present in the overview brand subset
  const filteredSkus = useMemo(() => {
    if (!skuWhitelist) return skus;
    return skus.filter(s => skuWhitelist.has(s.sku));
  }, [skus, skuWhitelist]);

  const channelAgg = useMemo(() => aggregateChannelKpis(filteredSkus), [filteredSkus]);

  // ============ ROW 1 KPIs ============
  // EXCLUSION: Steel Shakers + Glass Bottle Shaker parent SKUs are excluded from
  // the four KPI cards (Total Ecosystem / F2 + Pre-Pack / Channels Stock / values).
  // Their child SKUs are already counted separately. The exclusion only affects
  // these totals — the parent rows still appear in tables/views below.
  const row1 = useMemo(() => {
    let f2Current = 0, f2Prepack = 0, f2Value = 0;
    const headers = overviewRows[0] || [];
    const f2CurIdx = headers.findIndex(h => (h || '').toString().toLowerCase().includes('f2 current stock'));
    const f2PpIdx = headers.findIndex(h => (h || '').toString().toLowerCase().includes('f2 pre-packed stock'));
    const f2ValIdx = headers.findIndex(h => (h || '').toString().toLowerCase().includes('f2 inventory value'));
    const channelsValIdx = headers.findIndex(h => (h || '').toString().toLowerCase().includes('channels inventory value'));
    const skuIdx = headers.findIndex(h => ['sku', 'title', 'product name'].includes((h || '').toString().trim().toLowerCase()));
    let channelsValue = 0;
    let excludedChannelsStock = 0;
    (filteredOverviewRows.slice(1) || []).forEach(r => {
      const sku = skuIdx !== -1 ? r[skuIdx] : '';
      if (isExcludedFromTotals(sku)) {
        // Track how much channels-stock contribution we need to subtract below
        // (we can't subtract directly from overview.total since the channels map
        // is keyed by SKU and computed across ALL overview rows).
        excludedChannelsStock += (overview.map?.[(sku || '').toString().trim()] || 0);
        return;
      }
      f2Current += parseNumber(r[f2CurIdx]);
      f2Prepack += parseNumber(r[f2PpIdx]);
      f2Value += parseNumber(r[f2ValIdx]);
      channelsValue += parseNumber(r[channelsValIdx]);
    });
    const channelsStock = Math.max(0, (overview.total || 0) - excludedChannelsStock);
    const f2PrepackStock = f2Current + f2Prepack;
    const totalEcosystem = f2PrepackStock + channelsStock;

    // Total DRR = sum of all per-SKU per-channel DRRs (across the 8 channels)
    const totalDRR = CHANNEL_LIST.reduce((s, ch) => s + (channelAgg[ch]?.drr || 0), 0);
    const doh = (stock) => (totalDRR > 0 && stock > 0 ? Math.round(stock / totalDRR) : null);

    return {
      f2Current, f2Prepack, f2Value, f2PrepackStock,
      channelsStock, channelsValue,
      totalEcosystem, totalValue: f2Value + channelsValue,
      totalDRR,
      totalDOH: doh(totalEcosystem),
      f2DOH: doh(f2PrepackStock),
      channelsDOH: doh(channelsStock),
    };
  }, [filteredOverviewRows, overview, overviewRows, channelAgg]);

  // ============ ROW 3 Charts ============
  const pieData = useMemo(
    () => CHANNEL_LIST
      .map(ch => ({ name: dn(ch), key: ch, value: channelAgg[ch]?.stock || 0 }))
      .filter(d => d.value > 0),
    [channelAgg]
  );

  const scatterData = useMemo(
    () => CHANNEL_LIST
      .map(ch => ({
        channel: dn(ch),
        key: ch,
        stock: channelAgg[ch]?.stock || 0,
        sales: channelAgg[ch]?.sales30 || 0,
      }))
      .filter(d => d.stock > 0 || d.sales > 0),
    [channelAgg]
  );

  // Heatmap: top 15 SKUs by channelsStock × channels, cell color by DOH
  const heatmapSkus = useMemo(
    () => [...filteredSkus]
      .sort((a, b) => b.channelsStock - a.channelsStock)
      .slice(0, 15),
    [filteredSkus]
  );

  // Top Alerts — scan per-SKU per-channel rows where available stock > 0 & drr > 0.
  // Uses `c.stock` (available, excluding in-transit) so Critical SKU classification
  // matches the Channel cards' DOH and the user's "shippable today" mental model.
  const topAlerts = useMemo(() => {
    const rows = [];
    filteredSkus.forEach(s => {
      CHANNEL_LIST.forEach(ch => {
        const c = s.channels[ch];
        if (c.stock > 0 && c.drr > 0) {
          rows.push({ sku: s.sku, channel: dn(ch), doh: c.doh, ratio: c.salesStockRatio, stock: c.stock });
        }
      });
    });
    const critical = [...rows].filter(r => r.doh < 7).sort((a, b) => a.doh - b.doh).slice(0, 5);
    const overstock = [...rows].filter(r => r.doh > 45).sort((a, b) => b.doh - a.doh).slice(0, 5);
    const velocity = [...rows].sort((a, b) => b.ratio - a.ratio).slice(0, 5);
    return { critical, overstock, velocity };
  }, [filteredSkus]);

  // ============ Extended Inventory table dataset ============
  // Wide per-SKU view that joins F2 stock (from overview_source) with channel
  // total stock (sum across all channels in `filteredSkus`). DRR is the overall
  // SKU DRR = Σ channels[ch].drr. Two DOH columns differ only in their numerator:
  //   F2 DOH      = (Current + Pre-Pack) / DRR
  //   Overall DOH = Channel Total Stock / DRR
  // Both rounded; "—" when DRR = 0.
  //
  // Per-channel breakdown ({ stock, drr, doh } for each ch in CHANNEL_LIST) is
  // also attached on each row so the dynamic filter UI below can append
  // [Channel] Stock / DRR / DOH columns without re-computing.
  const extInvSortRef = useRef({ key: 'sku', dir: 'asc' });
  const [extInvSort, setExtInvSort] = useState({ key: 'sku', dir: 'asc' });
  extInvSortRef.current = extInvSort;

  // Dynamic column filter state for the Extended Inventory Table.
  // - extInvChannels: subset of CHANNEL_LIST. Empty array = "All Channels" mode.
  //   In "All Channels" mode, metric toggles auto-expand to every channel.
  // - extInvMetrics: which per-channel metrics to render (stock/drr/doh).
  //   All default OFF — user opts in to expose any per-channel columns.
  //   Examples:
  //     DOHs ON, no channels  → DOH column for every channel (8 cols)
  //     DRRs ON, Amazon only  → just Amazon DRR (1 col)
  //     All metrics, all 8    → 24 per-channel cols
  // - extInvBrands: row-filter pills. Empty = all brands visible.
  const [extInvChannels, setExtInvChannels] = useState([]);
  const [extInvMetrics, setExtInvMetrics] = useState({ stock: false, drr: false, doh: false, transit: false });
  const [extInvGroups, setExtInvGroups] = useState({ f2: false, cs: false });
  const [extInvBrands, setExtInvBrands] = useState([]);

  const extInvRows = useMemo(() => {
    const headers = overviewRows[0] || [];
    if (headers.length === 0) return [];
    const asinIdx = headers.findIndex(h => (h || '').toString().trim().toLowerCase() === 'asin');
    const brandIdx = headers.findIndex(h => (h || '').toString().trim().toLowerCase() === 'brand');
    const skuIdx = headers.findIndex(h => ['sku', 'title', 'product name'].includes((h || '').toString().trim().toLowerCase()));
    const f2CurIdx = headers.findIndex(h => (h || '').toString().toLowerCase().includes('f2 current stock'));
    const f2PpIdx  = headers.findIndex(h => (h || '').toString().toLowerCase().includes('f2 pre-packed stock'));
    if (skuIdx === -1) return [];

    // SKU → { drr, channelStock, channelTransit, byChannel: { [ch]: { stock, drr, doh, transit } } }
    const lookup = {};
    filteredSkus.forEach(s => {
      const drr = CHANNEL_LIST.reduce((acc, ch) => acc + (s.channels?.[ch]?.drr || 0), 0);
      const cs  = CHANNEL_LIST.reduce((acc, ch) => acc + (s.channels?.[ch]?.stock || 0), 0);
      const ct  = CHANNEL_LIST.reduce((acc, ch) => acc + (s.channels?.[ch]?.transit || 0), 0);
      const byChannel = {};
      CHANNEL_LIST.forEach(ch => {
        const c = s.channels?.[ch] || {};
        const cStock   = c.stock   || 0;
        const cDrr     = c.drr     || 0;
        const cTransit = c.transit || 0;
        byChannel[ch] = {
          stock: cStock,
          drr: cDrr,
          doh: cDrr > 0 && cStock > 0 ? Math.round(cStock / cDrr) : null,
          transit: cTransit,
        };
      });
      lookup[s.sku] = { drr, channelStock: cs, channelTransit: ct, byChannel };
    });

    return (filteredOverviewRows.slice(1) || [])
      .map(r => {
        const sku = (r[skuIdx] || '').toString().trim();
        if (!sku) return null;
        if (isExcludedFromTotals(sku)) return null;
        const current = f2CurIdx !== -1 ? parseNumber(r[f2CurIdx]) : 0;
        const prepack = f2PpIdx !== -1 ? parseNumber(r[f2PpIdx]) : 0;
        const { drr = 0, channelStock = 0, channelTransit = 0, byChannel = {} } = lookup[sku] || {};
        const f2Total = current + prepack;
        const f2Doh = drr > 0 ? Math.round(f2Total / drr) : null;
        const overallDoh = drr > 0 ? Math.round(channelStock / drr) : null;
        return {
          asin: asinIdx !== -1 ? (r[asinIdx] || '').toString().trim() : '',
          brand: brandIdx !== -1 ? (r[brandIdx] || '').toString().trim() : '',
          sku,
          drr,
          current,
          prepack,
          f2Doh,
          channelStock,
          channelTransit,
          overallDoh,
          byChannel,
        };
      })
      .filter(Boolean);
  }, [filteredOverviewRows, overviewRows, filteredSkus]);

  // Apply per-table brand pill row filter on top of extInvRows.
  const extInvFilteredRows = useMemo(() => {
    if (!extInvBrands.length) return extInvRows;
    const set = new Set(extInvBrands.map(b => b.toLowerCase()));
    return extInvRows.filter(r => set.has((r.brand || '').toLowerCase()));
  }, [extInvRows, extInvBrands]);

  // Unique brand list for the brand-pill row, derived from the (already
  // brand-filtered) extInvRows so it adapts to the global brand filter.
  const extInvBrandOptions = useMemo(() => {
    const set = new Set();
    extInvRows.forEach(r => { if (r.brand) set.add(r.brand); });
    return [...set].sort();
  }, [extInvRows]);

  // Sorted view — sort spec lives in state so each column header can toggle.
  // Supports both static keys (asin/brand/sku/drr/current/prepack/f2Doh/
  // channelStock/overallDoh) and dynamic per-channel keys of the form
  // `ch:<channelKey>:<stock|drr|doh>` produced by the channel filter columns.
  const extInvSorted = useMemo(() => {
    const cp = [...extInvFilteredRows];
    const { key, dir } = extInvSort;
    const dynamic = typeof key === 'string' && key.startsWith('ch:');
    const [, dynCh, dynMetric] = dynamic ? key.split(':') : [];
    const valOf = (row) => {
      if (dynamic) return row.byChannel?.[dynCh]?.[dynMetric];
      return row[key];
    };
    cp.sort((a, b) => {
      let av = valOf(a); let bv = valOf(b);
      if (av == null) av = Number.POSITIVE_INFINITY;
      if (bv == null) bv = Number.POSITIVE_INFINITY;
      if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return dir === 'asc' ? av - bv : bv - av;
    });
    return cp;
  }, [extInvFilteredRows, extInvSort]);

  const toggleExtInvSort = (key) => {
    setExtInvSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  // Footer totals — F2 and base columns sum directly. Per-channel totals are
  // computed in a separate map so the conditional <td> renderer in the tfoot
  // can pull `chTotals[ch].stock` / `.drr` without recomputing per cell.
  const extInvTotals = useMemo(() => {
    const base = {
      drr: 0, current: 0, prepack: 0, channelStock: 0, channelTransit: 0,
      chTotals: {},
    };
    CHANNEL_LIST.forEach(ch => { base.chTotals[ch] = { stock: 0, drr: 0, transit: 0 }; });
    extInvSorted.forEach(r => {
      base.drr += r.drr;
      base.current += r.current;
      base.prepack += r.prepack;
      base.channelStock += r.channelStock;
      base.channelTransit += r.channelTransit || 0;
      CHANNEL_LIST.forEach(ch => {
        base.chTotals[ch].stock   += r.byChannel?.[ch]?.stock   || 0;
        base.chTotals[ch].drr     += r.byChannel?.[ch]?.drr     || 0;
        base.chTotals[ch].transit += r.byChannel?.[ch]?.transit || 0;
      });
    });
    return base;
  }, [extInvSorted]);

  const hasData = overviewRows.length > 1;

  // ============ F2 + Pre-Pack details dataset ============
  // Joins overview_source (ASIN, Brand, F2 Current, F2 Pre-Packed) with the per-SKU
  // intelligence (DRR summed across channels) so DOH = (Current + Pre-Pack) / DRR.
  // Excluded parent SKUs (Steel Shakers / Glass Bottle Shaker) are filtered out —
  // they were never part of the KPI total. Modal-state lives here so the card click
  // can open it without re-fetching anything.
  const [f2Open, setF2Open] = useState(false);
  const [f2BrandSel, setF2BrandSel] = useState([]);
  const [f2SkuSel, setF2SkuSel] = useState([]);

  const f2Details = useMemo(() => {
    const headers = overviewRows[0] || [];
    if (headers.length === 0) return [];
    const asinIdx = headers.findIndex(h => (h || '').toString().trim().toLowerCase() === 'asin');
    const brandIdx = headers.findIndex(h => (h || '').toString().trim().toLowerCase() === 'brand');
    const skuIdx = headers.findIndex(h => ['sku', 'title', 'product name'].includes((h || '').toString().trim().toLowerCase()));
    const f2CurIdx = headers.findIndex(h => (h || '').toString().toLowerCase().includes('f2 current stock'));
    const f2PpIdx  = headers.findIndex(h => (h || '').toString().toLowerCase().includes('f2 pre-packed stock'));
    if (skuIdx === -1) return [];

    // SKU → summed DRR across channels (used as denominator in the DOH formula).
    const drrBySku = {};
    filteredSkus.forEach(s => {
      const total = CHANNEL_LIST.reduce((acc, ch) => acc + (s.channels?.[ch]?.drr || 0), 0);
      drrBySku[s.sku] = total;
    });

    return (filteredOverviewRows.slice(1) || [])
      .map(r => {
        const sku = (r[skuIdx] || '').toString().trim();
        if (!sku) return null;
        if (isExcludedFromTotals(sku)) return null;     // parents excluded from total
        const current = f2CurIdx !== -1 ? parseNumber(r[f2CurIdx]) : 0;
        const prepack = f2PpIdx !== -1 ? parseNumber(r[f2PpIdx]) : 0;
        const drr = drrBySku[sku] || 0;
        const total = current + prepack;
        const doh = drr > 0 ? Math.round(total / drr) : null;
        return {
          asin: asinIdx !== -1 ? (r[asinIdx] || '').toString().trim() : '',
          brand: brandIdx !== -1 ? (r[brandIdx] || '').toString().trim() : '',
          sku,
          current,
          prepack,
          drr,
          doh,
        };
      })
      .filter(Boolean);
  }, [filteredOverviewRows, overviewRows, filteredSkus]);

  // Brand / SKU options for the modal's filters.
  const f2BrandOptions = useMemo(() => {
    const s = new Set();
    f2Details.forEach(r => { if (r.brand) s.add(r.brand); });
    return [...s].sort().map(b => ({ value: b, label: b }));
  }, [f2Details]);
  const f2SkuOptions = useMemo(
    () => f2Details.map(r => ({ value: r.sku, label: r.sku })).sort((a, b) => a.label.localeCompare(b.label)),
    [f2Details],
  );

  const f2DetailsFiltered = useMemo(() => {
    let out = f2Details;
    if (f2BrandSel.length > 0) out = out.filter(r => f2BrandSel.includes(r.brand));
    if (f2SkuSel.length > 0) out = out.filter(r => f2SkuSel.includes(r.sku));
    return out;
  }, [f2Details, f2BrandSel, f2SkuSel]);

  const f2Totals = useMemo(() => ({
    current: f2DetailsFiltered.reduce((s, r) => s + r.current, 0),
    prepack: f2DetailsFiltered.reduce((s, r) => s + r.prepack, 0),
  }), [f2DetailsFiltered]);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border" data-testid="dashboard-header">
        <div className="max-w-[1900px] mx-auto p-4 md:p-6 flex items-center justify-between">
          <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-wv-ink">RADAR Dashboard</h1>
          <Button onClick={fetchAll} disabled={loading} data-testid="refresh-button">
            <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </header>

      {hasData && (
        <div className="max-w-[1900px] mx-auto px-4 md:px-6 pt-4">
          <div className="flex items-center gap-3">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Brand</Label>
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="w-[220px]" data-testid="brand-filter"><SelectValue placeholder="All Brands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <main className="max-w-[1900px] mx-auto p-4 md:p-6 space-y-8">
        {!hasData && !loading && (
          <Card className="max-w-md mx-auto text-center mt-16">
            <CardHeader>
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-wv-muted" />
              <CardTitle>No Data Available</CardTitle>
            </CardHeader>
            <CardContent>
              <Button onClick={fetchAll}><RefreshCw className="mr-2" size={16} /> Retry</Button>
            </CardContent>
          </Card>
        )}

        {hasData && (
          <>
            {/* ============ ROW 1: Ecosystem KPIs ============ */}
            <section>
              <h2 className="text-lg md:text-xl font-semibold text-wv-ink mb-4">Ecosystem Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-border" data-testid="kpi-total-ecosystem">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Total Ecosystem</p>
                      <Boxes size={18} className="text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-semibold tracking-tight text-wv-ink" data-testid="total-ecosystem-value">{formatInt(row1.totalEcosystem)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Units (F2 + Pre-packed + Channels)</p>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-sm text-wv-body">Value: {formatINR(row1.totalValue)}</p>
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${row1.totalDOH != null ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-[#F1F5F9] text-[#64748B]'}`} data-testid="total-ecosystem-doh">
                        DOH: {row1.totalDOH ?? '—'}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="border-border cursor-pointer hover:shadow-md hover:border-primary/50 transition-all"
                  data-testid="kpi-f2-prepack"
                  onClick={() => setF2Open(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setF2Open(true); } }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">F2 + Pre-Pack Stock</p>
                      <Warehouse size={18} className="text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-semibold tracking-tight text-wv-ink">{formatInt(row1.f2PrepackStock)}</p>
                    <p className="text-xs text-muted-foreground mt-1">F2 Current + F2 Pre-Packed · Click for SKU breakdown</p>
                    <div className="flex justify-between text-sm text-wv-body mt-3">
                      <span>Current: {formatInt(row1.f2Current)}</span>
                      <span>Pre-Pack: {formatInt(row1.f2Prepack)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm text-wv-body">Value: {formatINR(row1.f2Value)}</p>
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${row1.f2DOH != null ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-[#F1F5F9] text-[#64748B]'}`} data-testid="f2-prepack-doh">
                        DOH: {row1.f2DOH ?? '—'}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border" data-testid="kpi-channels-stock">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Channels Stock</p>
                      <Store size={18} className="text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-semibold tracking-tight text-wv-ink" data-testid="channels-stock-value">{formatInt(row1.channelsStock)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {overview.derived
                        ? 'Derived: Total Current Stock − F2 − Pre-Packed (add "Channels Stock" to overview_source Col V)'
                        : 'Source: overview_source → "Channels Stock" (Col V)'}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-sm text-wv-body">Value: {formatINR(row1.channelsValue)}</p>
                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${row1.channelsDOH != null ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-[#F1F5F9] text-[#64748B]'}`} data-testid="channels-doh">
                        DOH: {row1.channelsDOH ?? '—'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* ============ ROW 2: Channel Intelligence ============ */}
            <section>
              <h2 className="text-lg md:text-xl font-semibold text-wv-ink mb-4">Channel Intelligence</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                {CHANNEL_LIST.map(ch => {
                  const k = channelAgg[ch];
                  if (!k) return null;
                  const status = classifyStatus(k.doh, k.stock);
                  return (
                    <Card key={ch} className="border-border" data-testid={`channel-card-${ch.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-wv-ink">{dn(ch)}</p>
                          <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_STYLES[status.tone]}`}>{status.label}</span>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-2xl font-bold tracking-tight text-wv-ink" data-testid={`channel-available-stock-${ch.toLowerCase().replace(/[^a-z]/g, '-')}`}>{formatInt(k.stock)}</p>
                          <p className="text-xs text-muted-foreground">Available Stock Units <span className="text-[10px] opacity-70">(excl. in-transit)</span></p>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                          <div>
                            <p className="text-muted-foreground">Value <span className="text-[10px] opacity-70">(avail.)</span></p>
                            <p className="text-wv-body font-medium">{formatINR(k.value)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">DRR</p>
                            <p className="text-wv-body font-medium">{formatInt(k.drr)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">DOH</p>
                            <p className={`inline-block px-2 rounded font-semibold ${DOH_CELL(k.doh, k.stock)}`}>
                              {formatDoh(k.doh, k.stock)}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Sales 30D</p>
                            <p className="text-wv-body font-medium">{formatInt(k.sales30)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">In-Transit Units</p>
                            <p className="text-wv-body font-medium" data-testid={`channel-transit-${ch.toLowerCase().replace(/[^a-z]/g, '-')}`}>{formatInt(k.transit || 0)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Stock:Sales</p>
                            <p className="text-wv-body font-medium">{k.salesStockRatio > 0 ? (1 / k.salesStockRatio).toFixed(2) : '—'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>

            {/* ============ Extended Inventory Table ============
                Wide per-SKU view with merged headers, frozen ASIN/Brand/SKU cols,
                sticky table header, sortable columns, and a totals footer.
                Filter UI sits above the card:
                  • Channel pills (multi-select)
                  • Metric toggles (Stock / DRR / DOH) — apply to every selected channel
                  • Brand pills (row filter)
                Default view (no channels) = Base info + F2 columns + Channel Total
                Stock + Overall DOH. Selecting a channel appends per-channel sub-cols. */}
            <section data-testid="extended-inventory-section">
              <h2 className="text-lg md:text-xl font-semibold text-wv-ink mb-4">Extended Inventory Table</h2>

              {/* ---------- Filter Bar ---------- */}
              <div className="space-y-3 mb-4" data-testid="ext-inv-filter-bar">
                {/* Groups (F2 + Channel Stocks) — toggle pills, same UX as channels.
                    When a metric is selected, these pills control whether F2 / CS
                    group columns are rendered. When no metric is active, the
                    pills are ignored and the default view shows all 5 base cols. */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground mr-1">Groups</span>
                  {[
                    { key: 'f2', label: 'F2' },
                    { key: 'cs', label: 'Channel Stocks' },
                  ].map(g => {
                    const active = extInvGroups[g.key];
                    return (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() => setExtInvGroups(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-wv-body border-border hover:border-primary/60'}`}
                        data-testid={`ext-inv-group-${g.key}`}
                      >
                        {g.label}
                      </button>
                    );
                  })}
                </div>

                {/* Channels */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground mr-1">Channels</span>
                  <button
                    type="button"
                    onClick={() => setExtInvChannels([])}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${extInvChannels.length === 0 ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-wv-body border-border hover:border-primary/60'}`}
                    data-testid="ext-inv-channel-all"
                  >
                    All Channels
                  </button>
                  {CHANNEL_LIST.map(ch => {
                    const active = extInvChannels.includes(ch);
                    return (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => setExtInvChannels(prev => active ? prev.filter(c => c !== ch) : [...prev, ch])}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-wv-body border-border hover:border-primary/60'}`}
                        data-testid={`ext-inv-channel-${ch.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {dn(ch)}
                      </button>
                    );
                  })}
                </div>

                {/* Metrics — toggling on with no channel selected expands the
                    metric across ALL channels; with channel pills selected, the
                    metric is scoped to just those channels. Each metric has a
                    distinct light cell-background for at-a-glance scanning. */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground mr-1">Metrics</span>
                  {[
                    { key: 'stock',   label: 'Stock',     swatch: 'bg-sky-200'    },
                    { key: 'drr',     label: 'DRRs',      swatch: 'bg-amber-200'  },
                    { key: 'doh',     label: 'DOHs',      swatch: 'bg-emerald-200'},
                    { key: 'transit', label: 'In-Transit',swatch: 'bg-violet-200' },
                  ].map(m => {
                    const active = extInvMetrics[m.key];
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setExtInvMetrics(prev => ({ ...prev, [m.key]: !prev[m.key] }))}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors inline-flex items-center gap-1.5 ${active ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-wv-body border-border hover:border-emerald-700/60'}`}
                        data-testid={`ext-inv-metric-${m.key}`}
                      >
                        <span className={`inline-block w-2 h-2 rounded-full ${m.swatch}`} aria-hidden="true" />
                        {m.label}
                      </button>
                    );
                  })}
                  <span className="text-[11px] text-muted-foreground italic ml-2">
                    {(extInvChannels.length === 0 && !extInvGroups.f2 && !extInvGroups.cs)
                      ? 'Toggle a metric to add columns for all channels'
                      : `Scoped to ${extInvGroups.f2 ? 'F2 · ' : ''}${extInvGroups.cs ? 'Channel Stocks · ' : ''}${extInvChannels.length} channel${extInvChannels.length === 1 ? '' : 's'}`}
                  </span>
                </div>

                {/* Brands */}
                {extInvBrandOptions.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] uppercase tracking-widest text-muted-foreground mr-1">Brand</span>
                    <button
                      type="button"
                      onClick={() => setExtInvBrands([])}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${extInvBrands.length === 0 ? 'bg-wv-ink text-white border-wv-ink' : 'bg-white text-wv-body border-border hover:border-wv-ink/60'}`}
                      data-testid="ext-inv-brand-all"
                    >
                      All Brands
                    </button>
                    {extInvBrandOptions.map(b => {
                      const active = extInvBrands.includes(b);
                      return (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setExtInvBrands(prev => active ? prev.filter(x => x !== b) : [...prev, b])}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-wv-ink text-white border-wv-ink' : 'bg-white text-wv-body border-border hover:border-wv-ink/60'}`}
                          data-testid={`ext-inv-brand-${b.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {b}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <Card className="overflow-hidden">
                <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
                  {(() => {
                    // Dynamic sub-columns per selected channel — order respects
                    // CHANNEL_LIST (not click order) for visual consistency.
                    // If no channel pill is selected, treat as "All Channels" so
                    // metric toggles automatically expand across every channel.
                    const metricList = [
                      { key: 'stock',   label: 'Stock'      },
                      { key: 'drr',     label: 'DRR'        },
                      { key: 'doh',     label: 'DOH'        },
                      { key: 'transit', label: 'In-Transit' },
                    ].filter(m => extInvMetrics[m.key]);
                    const anyMetric = metricList.length > 0;
                    // Per-metric light tint applied to header sub-cells, body
                    // cells, and footer cells so a quick glance separates Stock
                    // (sky) / DRR (amber) / DOH (emerald) / In-Transit (violet).
                    const metricTintHeader = { stock: 'bg-sky-100', drr: 'bg-amber-100', doh: 'bg-emerald-100', transit: 'bg-violet-100' };
                    const metricTintCell   = { stock: 'bg-sky-50',  drr: 'bg-amber-50',  doh: 'bg-emerald-50',  transit: 'bg-violet-50'  };
                    const metricTintTotal  = { stock: 'bg-sky-100/30', drr: 'bg-amber-100/30', doh: 'bg-emerald-100/30', transit: 'bg-violet-100/30' };
                    // Group selection is the SOLE controller of F2 and Channel
                    // Stocks visibility. Neither section is shown by default;
                    // toggling a group pill is the only way to bring it in.
                    const showF2 = extInvGroups.f2;
                    const showCs = extInvGroups.cs;
                    const orderedChannels = !anyMetric
                      ? []  // no metrics on = no per-channel columns even if channels picked
                      : (extInvChannels.length === 0
                          ? [...CHANNEL_LIST]
                          : CHANNEL_LIST.filter(ch => extInvChannels.includes(ch)));

                    // Base sub-cols: group toggle gates visibility, metrics filter
                    // within. When a group is on but NO metric is active, show
                    // that group's full default set of detail columns.
                    //   F2 group  → Current Stock, Pre-Packed Stock, F2 DOH
                    //   CS group  → Channel Total Stock, Overall DOH (+ Total
                    //               Channel In-Transit when transit metric is on)
                    // When a metric is active, only that metric's columns render
                    // within each toggled group.
                    const buildBaseSubCols = () => {
                      const cols = [];
                      // ---- F2 group ----
                      if (showF2) {
                        if (!anyMetric || extInvMetrics.stock) {
                          cols.push(
                            { key: 'current', label: 'Current Stock',    group: 'f2', metric: 'stock' },
                            { key: 'prepack', label: 'Pre-Packed Stock', group: 'f2', metric: 'stock' },
                          );
                        }
                        if (!anyMetric || extInvMetrics.doh) {
                          cols.push({ key: 'f2Doh', label: 'F2 DOH', group: 'f2', metric: 'doh' });
                        }
                        // DRR / In-Transit have no F2-grain column.
                      }
                      // ---- Channel Stocks group ----
                      if (showCs) {
                        if (!anyMetric || extInvMetrics.stock) {
                          cols.push({ key: 'channelStock', label: 'Channel Total Stock', group: 'cs', metric: 'stock' });
                        }
                        if (!anyMetric || extInvMetrics.doh) {
                          cols.push({ key: 'overallDoh', label: 'Overall DOH', group: 'cs', metric: 'doh' });
                        }
                        if (extInvMetrics.transit) {
                          cols.push({ key: 'channelTransit', label: 'Total Channel In-Transit', group: 'cs', metric: 'transit' });
                        }
                        // DRR has no extra CS column — Overall SKU DRR is permanent.
                      }
                      return cols;
                    };
                    const baseSubCols = buildBaseSubCols();
                    // Mark the last col in each group so we can paint a divider.
                    const lastF2Idx = (() => {
                      let idx = -1; baseSubCols.forEach((c, i) => { if (c.group === 'f2') idx = i; });
                      return idx;
                    })();
                    const lastCsIdx = (() => {
                      let idx = -1; baseSubCols.forEach((c, i) => { if (c.group === 'cs') idx = i; });
                      return idx;
                    })();
                    const f2Span = baseSubCols.filter(c => c.group === 'f2').length;
                    const csSpan = baseSubCols.filter(c => c.group === 'cs').length;

                    // Total column count for empty-state colspan
                    const totalColCount = 4 + baseSubCols.length + orderedChannels.length * metricList.length;

                    const f2Bg = 'bg-primary/90';
                    const csBg = 'bg-emerald-700';
                    const chBg = 'bg-sky-700';

                    return (
                      <table className="w-full border-collapse text-sm" data-testid="extended-inventory-table">
                        <thead className="sticky top-0 z-30">
                          <tr className="bg-primary text-primary-foreground">
                            {[
                              { key: 'asin',  label: 'ASIN',             left: 0,   width: 140 },
                              { key: 'brand', label: 'Brand',            left: 140, width: 140 },
                              { key: 'sku',   label: 'SKU',              left: 280, width: 320 },
                              { key: 'drr',   label: 'Overall SKU DRR',  left: null, width: 140 },
                            ].map((c) => {
                              const active = extInvSort.key === c.key;
                              const Icon = active ? (extInvSort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                              const sticky = c.left != null;
                              return (
                                <th
                                  key={c.key}
                                  rowSpan={2}
                                  onClick={() => toggleExtInvSort(c.key)}
                                  className={`px-3 py-2 text-xs font-semibold uppercase tracking-widest cursor-pointer hover:bg-primary/90 ${sticky ? 'sticky z-40 border-r border-primary/40' : ''} ${c.key === 'sku' ? 'border-r-2 border-primary-foreground/30' : ''}`}
                                  style={{ width: c.width, minWidth: c.width, maxWidth: c.width, left: c.left ?? undefined, backgroundColor: '#2EB4AC' }}
                                  data-testid={`ext-inv-sort-${c.key}`}
                                >
                                  <div className={`flex items-center gap-1 ${c.key === 'drr' ? 'justify-end' : 'justify-start'}`}>
                                    <span>{c.label}</span>
                                    <Icon size={12} className={active ? '' : 'opacity-50'} />
                                  </div>
                                </th>
                              );
                            })}
                            {f2Span > 0 && (
                              <th colSpan={f2Span} className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-widest border-r border-primary/40 bg-primary">F2</th>
                            )}
                            {csSpan > 0 && (
                              <th colSpan={csSpan} className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-widest bg-emerald-700">Channel Stocks</th>
                            )}
                            {orderedChannels.map(ch => (
                              <th
                                key={`grp-${ch}`}
                                colSpan={metricList.length || 1}
                                className={`px-3 py-2 text-center text-xs font-semibold uppercase tracking-widest ${chBg} border-l border-white/20`}
                              >
                                {dn(ch)}
                              </th>
                            ))}
                          </tr>
                          <tr className="bg-primary/90 text-primary-foreground">
                            {baseSubCols.map((sc, i) => {
                              const active = extInvSort.key === sc.key;
                              const Icon = active ? (extInvSort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                              const bg = sc.group === 'f2' ? f2Bg : csBg;
                              const dividerCls = (i === lastF2Idx && f2Span > 0 && csSpan > 0) ? 'border-r border-primary/40' : '';
                              return (
                                <th
                                  key={sc.key}
                                  onClick={() => toggleExtInvSort(sc.key)}
                                  className={`px-3 py-2 text-right text-[11px] font-semibold tracking-wide cursor-pointer hover:opacity-90 ${bg} ${dividerCls}`}
                                  data-testid={`ext-inv-sort-${sc.key}`}
                                >
                                  <div className="flex items-center justify-end gap-1">
                                    <span>{sc.label}</span>
                                    <Icon size={12} className={active ? '' : 'opacity-50'} />
                                  </div>
                                </th>
                              );
                            })}
                            {orderedChannels.flatMap(ch => (
                              metricList.length === 0
                                ? [<th key={`empty-${ch}`} className={`px-3 py-2 text-right text-[11px] ${chBg}`}>&nbsp;</th>]
                                : metricList.map(m => {
                                    const sortKey = `ch:${ch}:${m.key}`;
                                    const active = extInvSort.key === sortKey;
                                    const Icon = active ? (extInvSort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                                    return (
                                      <th
                                        key={`${ch}-${m.key}`}
                                        onClick={() => toggleExtInvSort(sortKey)}
                                        className={`px-3 py-2 text-right text-[11px] font-semibold tracking-wide cursor-pointer hover:opacity-90 ${chBg} border-l border-white/10`}
                                        data-testid={`ext-inv-sort-${ch.replace(/\s+/g, '-').toLowerCase()}-${m.key}`}
                                      >
                                        <div className="flex items-center justify-end gap-1">
                                          <span>{m.label}</span>
                                          <Icon size={12} className={active ? '' : 'opacity-50'} />
                                        </div>
                                      </th>
                                    );
                                  })
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {extInvSorted.length === 0 && (
                            <tr>
                              <td colSpan={totalColCount} className="text-center py-8 text-muted-foreground">No SKUs to display</td>
                            </tr>
                          )}
                          {extInvSorted.map((r, ri) => {
                            const rowBg = ri % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                            return (
                              <tr key={`${r.asin || 'no-asin'}-${r.sku}-${ri}`} className={rowBg} data-testid={`ext-inv-row-${ri}`}>
                                <td className={`px-3 py-2 text-xs text-wv-ink whitespace-nowrap sticky left-0 z-10 ${rowBg}`} style={{ width: 140, minWidth: 140, maxWidth: 140 }}>{r.asin || '—'}</td>
                                <td className={`px-3 py-2 text-xs text-wv-body whitespace-nowrap sticky z-10 ${rowBg}`} style={{ left: 140, width: 140, minWidth: 140, maxWidth: 140 }}>{r.brand || '—'}</td>
                                <td className={`px-3 py-2 text-xs text-wv-ink sticky z-10 border-r-2 border-border ${rowBg}`} style={{ left: 280, width: 320, minWidth: 320, maxWidth: 320 }}>
                                  <span className="truncate block">{r.sku}</span>
                                </td>
                                <td className="px-3 py-2 text-right text-xs tabular-nums text-wv-body">{formatInt(r.drr)}</td>
                                {baseSubCols.map((sc, i) => {
                                  const isCs = sc.group === 'cs';
                                  const dividerCls = (i === lastF2Idx && f2Span > 0 && csSpan > 0) ? 'border-r border-border' : '';
                                  const txtCls = isCs ? 'text-emerald-900 font-medium' : 'text-wv-body';
                                  const tintCls = sc.metric ? metricTintCell[sc.metric] : '';
                                  let val;
                                  if (sc.key === 'f2Doh')          val = r.f2Doh      == null ? '—' : r.f2Doh;
                                  else if (sc.key === 'overallDoh') val = r.overallDoh == null ? '—' : r.overallDoh;
                                  else                              val = formatInt(r[sc.key] || 0);
                                  return (
                                    <td key={sc.key} className={`px-3 py-2 text-right text-xs tabular-nums ${txtCls} ${tintCls} ${dividerCls}`}>
                                      {val}
                                    </td>
                                  );
                                })}
                                {orderedChannels.flatMap(ch => {
                                  const bc = r.byChannel?.[ch] || { stock: 0, drr: 0, doh: null, transit: 0 };
                                  if (metricList.length === 0) {
                                    return [<td key={`empty-${ch}`} className="px-3 py-2 text-right text-xs tabular-nums text-sky-900">&nbsp;</td>];
                                  }
                                  return metricList.map(m => (
                                    <td
                                      key={`${ch}-${m.key}`}
                                      className={`px-3 py-2 text-right text-xs tabular-nums text-sky-900 border-l border-border/60 ${metricTintCell[m.key] || ''}`}
                                      data-testid={`ext-inv-cell-${ri}-${ch.replace(/\s+/g, '-').toLowerCase()}-${m.key}`}
                                    >
                                      {m.key === 'doh'
                                        ? (bc.doh == null ? '—' : bc.doh)
                                        : formatInt(bc[m.key] || 0)}
                                    </td>
                                  ));
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                        {extInvSorted.length > 0 && (
                          <tfoot className="sticky bottom-0 z-20">
                            <tr className="bg-wv-ink text-white font-semibold">
                              <td className="px-3 py-2 text-xs uppercase tracking-widest sticky left-0 z-30 bg-wv-ink" style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</td>
                              <td className="bg-wv-ink sticky z-30" style={{ left: 140, width: 140, minWidth: 140, maxWidth: 140 }}>&nbsp;</td>
                              <td className="bg-wv-ink sticky z-30 border-r-2 border-white/20" style={{ left: 280, width: 320, minWidth: 320, maxWidth: 320 }}>&nbsp;</td>
                              <td className="px-3 py-2 text-right text-xs tabular-nums" data-testid="ext-inv-total-drr">{formatInt(extInvTotals.drr)}</td>
                              {baseSubCols.map((sc, i) => {
                                const dividerCls = (i === lastF2Idx && f2Span > 0 && csSpan > 0) ? 'border-r border-white/20' : '';
                                const tintCls = sc.metric ? metricTintTotal[sc.metric] : '';
                                let val;
                                if (sc.key === 'f2Doh') {
                                  val = extInvTotals.drr > 0
                                    ? Math.round((extInvTotals.current + extInvTotals.prepack) / extInvTotals.drr)
                                    : '—';
                                } else if (sc.key === 'overallDoh') {
                                  val = extInvTotals.drr > 0
                                    ? Math.round(extInvTotals.channelStock / extInvTotals.drr)
                                    : '—';
                                } else {
                                  val = formatInt(extInvTotals[sc.key] || 0);
                                }
                                return (
                                  <td
                                    key={sc.key}
                                    className={`px-3 py-2 text-right text-xs tabular-nums ${tintCls} ${dividerCls}`}
                                    data-testid={`ext-inv-total-${sc.key}`}
                                  >
                                    {val}
                                  </td>
                                );
                              })}
                              {orderedChannels.flatMap(ch => {
                                const t = extInvTotals.chTotals[ch] || { stock: 0, drr: 0, transit: 0 };
                                if (metricList.length === 0) {
                                  return [<td key={`empty-tot-${ch}`} className="px-3 py-2 text-right text-xs tabular-nums">&nbsp;</td>];
                                }
                                return metricList.map(m => (
                                  <td
                                    key={`tot-${ch}-${m.key}`}
                                    className={`px-3 py-2 text-right text-xs tabular-nums border-l border-white/10 ${metricTintTotal[m.key] || ''}`}
                                    data-testid={`ext-inv-total-${ch.replace(/\s+/g, '-').toLowerCase()}-${m.key}`}
                                  >
                                    {m.key === 'doh'
                                      ? (t.drr > 0 ? Math.round(t.stock / t.drr) : '—')
                                      : formatInt(t[m.key] || 0)}
                                  </td>
                                ));
                              })}
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    );
                  })()}
                </div>
              </Card>
            </section>

            {/* ============ ROW 3: Visualizations ============ */}
            <section>
              <h2 className="text-lg md:text-xl font-semibold text-wv-ink mb-4">Analytics</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie: Channel-wise Stock Distribution */}
                <Card className="border-border" data-testid="pie-stock-distribution">
                  <CardHeader><CardTitle className="text-base font-semibold">Channel-wise Stock Distribution</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          cx="50%" cy="50%"
                          outerRadius={100}
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieData.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => formatInt(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Scatter: Sales vs Stock */}
                <Card className="border-border" data-testid="scatter-sales-vs-stock">
                  <CardHeader><CardTitle className="text-base font-semibold">Sales (30D) vs Stock — per Channel</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis type="number" dataKey="stock" name="Stock" tick={{ fontSize: 12, fill: '#64748B' }} />
                        <YAxis type="number" dataKey="sales" name="Sales 30D" tick={{ fontSize: 12, fill: '#64748B' }} />
                        <ZAxis type="category" dataKey="channel" name="Channel" />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, n) => [formatInt(v), n]} />
                        <Legend />
                        <Scatter name="Channels" data={scatterData} fill="#2EB4AC">
                          {scatterData.map((entry, i) => <Cell key={entry.channel} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* DOH Heatmap */}
                <Card className="border-border lg:col-span-2" data-testid="heatmap-doh">
                  <CardHeader><CardTitle className="text-base font-semibold">DOH Heatmap (Top 15 SKUs × Channels)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground">
                            <th className="text-left p-2 font-semibold sticky left-0 bg-card">SKU</th>
                            {CHANNEL_LIST.map(ch => (
                              <th key={ch} className="text-center p-2 font-semibold">{dn(ch)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {heatmapSkus.map(s => (
                            <tr key={s.sku} className="border-t border-border">
                              <td className="p-2 font-medium sticky left-0 bg-card text-wv-body">{s.sku}</td>
                              {CHANNEL_LIST.map(ch => {
                                const c = s.channels[ch];
                                return (
                                  <td key={ch} className="p-1 text-center">
                                    <span className={`inline-block px-2 py-1 rounded font-medium min-w-[44px] ${DOH_CELL(c.doh, c.stock)}`}>
                                      {formatDoh(c.doh, c.stock)}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                          {heatmapSkus.length === 0 && (
                            <tr><td colSpan={CHANNEL_LIST.length + 1} className="p-4 text-center text-muted-foreground">No SKUs available</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
                      <span>Legend:</span>
                      <span className="px-2 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C]">&lt; 7 Critical</span>
                      <span className="px-2 py-0.5 rounded bg-[#FFEDD5] text-[#C2410C]">7–15 Risk</span>
                      <span className="px-2 py-0.5 rounded bg-[#DCFCE7] text-[#15803D]">15–45 Healthy</span>
                      <span className="px-2 py-0.5 rounded bg-[#DBEAFE] text-[#1D4ED8]">&gt; 45 Excess</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Alerts */}
                <Card className="border-border lg:col-span-2" data-testid="top-alerts">
                  <CardHeader><CardTitle className="text-base font-semibold">Top Alerts</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <AlertList title="Critical" tone="red" Icon={AlertTriangle} rows={topAlerts.critical} metric="doh" unit="d DOH" />
                      <AlertList title="Overstock" tone="blue" Icon={PackageX} rows={topAlerts.overstock} metric="doh" unit="d DOH" />
                      <AlertList title="High Velocity" tone="green" Icon={TrendingUp} rows={topAlerts.velocity} metric="ratio" unit="× S:S" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>
          </>
        )}
      </main>

      {/* F2 + Pre-Pack drill-down — SKU-wise table with brand/SKU filters, sorting,
          sticky headers, zebra rows, totals (matches DataTable styling used elsewhere). */}
      <Dialog open={f2Open} onOpenChange={setF2Open}>
        <DialogContent className="max-w-[1300px] w-[95vw]" data-testid="f2-prepack-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-wv-ink">
              F2 + Pre-Pack Stock — SKU breakdown
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                ({formatInt(f2Totals.current + f2Totals.prepack)} units total)
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 block">Brand</Label>
              <MultiSelect
                testId="f2-brand-filter"
                options={f2BrandOptions}
                selected={f2BrandSel}
                onChange={setF2BrandSel}
                placeholder="All brands"
                emptyLabel="All brands"
                searchable
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 block">SKU</Label>
              <MultiSelect
                testId="f2-sku-filter"
                options={f2SkuOptions}
                selected={f2SkuSel}
                onChange={setF2SkuSel}
                placeholder="All SKUs"
                emptyLabel="All SKUs"
                searchable
              />
            </div>
          </div>
          <DataTable
            testId="f2-prepack-table"
            searchKeys={['asin', 'brand', 'sku']}
            searchPlaceholder="Search ASIN / brand / SKU…"
            pageSizeOptions={[15, 30, 50, 100]}
            defaultPageSize={15}
            maxHeight="60vh"
            rows={f2DetailsFiltered.map((r, i) => ({ ...r, __key: `${r.asin || 'no-asin'}-${r.sku}-${i}` }))}
            renderTopRow={(rows) => {
              const c = rows.reduce((s, x) => s + x.current, 0);
              const p = rows.reduce((s, x) => s + x.prepack, 0);
              const t = c + p;
              const dSum = rows.reduce((s, x) => s + x.drr, 0);
              const dohAgg = dSum > 0 ? Math.round(t / dSum) : null;
              return (
                <>
                  <td className="px-3 py-2 text-[13px] font-semibold text-white" colSpan={3} style={{ whiteSpace: 'nowrap' }}>Total</td>
                  <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white">{formatInt(c)}</td>
                  <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white">{formatInt(p)}</td>
                  <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white">{dohAgg == null ? '—' : `${dohAgg} d`}</td>
                </>
              );
            }}
            columns={[
              {
                key: 'asin', header: 'ASIN', align: 'left', width: '14%', sortable: true,
                render: (r) => <span className="text-xs">{r.asin || '—'}</span>,
              },
              {
                key: 'brand', header: 'Brand', align: 'left', width: '14%', sortable: true,
                render: (r) => <span className="text-xs">{r.brand || '—'}</span>,
              },
              {
                key: 'sku', header: 'SKU Name', align: 'left', width: '32%', sortable: true,
                render: (r) => <span className="text-xs text-wv-ink">{r.sku}</span>,
              },
              {
                key: 'current', header: 'Current Stock', align: 'right', width: '13%', sortable: true,
                sortAccessor: (r) => r.current,
                render: (r) => <span className="tabular-nums text-xs">{formatInt(r.current)}</span>,
              },
              {
                key: 'prepack', header: 'Pre-Packed Stock', align: 'right', width: '13%', sortable: true,
                sortAccessor: (r) => r.prepack,
                render: (r) => <span className="tabular-nums text-xs">{formatInt(r.prepack)}</span>,
              },
              {
                key: 'doh', header: 'DOH (days)', align: 'right', width: '14%', sortable: true,
                // Null DOH (drr=0) sorts last in ascending order.
                sortAccessor: (r) => (r.doh == null ? Number.POSITIVE_INFINITY : r.doh),
                render: (r) => r.doh == null
                  ? <span className="text-[#64748B] text-xs">—</span>
                  : <span className="tabular-nums text-xs">{r.doh}</span>,
              },
            ]}
            emptyMessage="No SKUs match the selected filters"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AlertList = ({ title, tone, Icon, rows, metric, unit }) => (
  <div className="border border-border rounded-lg p-3" data-testid={`alert-list-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm font-semibold text-wv-ink flex items-center gap-2">
        <Icon size={16} className={
          tone === 'red' ? 'text-[#B91C1C]'
          : tone === 'blue' ? 'text-[#1D4ED8]'
          : 'text-[#15803D]'
        } />
        {title}
      </p>
      <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_STYLES[tone]}`}>{rows.length}</span>
    </div>
    <ul className="space-y-1 text-xs">
      {rows.length === 0 && <li className="text-muted-foreground">No items</li>}
      {rows.map((r, i) => (
        <li key={`${r.sku}-${r.channel}-${i}`} className="flex items-center justify-between gap-2">
          <span className="truncate text-wv-body" title={r.sku}>{r.sku}</span>
          <span className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] text-muted-foreground">{r.channel}</span>
            <span className={`px-1.5 py-0.5 rounded font-semibold ${STATUS_STYLES[tone]}`}>
              {metric === 'ratio' ? r.ratio.toFixed(2) : Math.round(r.doh)}{unit}
            </span>
          </span>
        </li>
      ))}
    </ul>
  </div>
);
